import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ResponsiveShell from '../components/layout/ResponsiveShell'
import useFaqCapabilities from '../components/faq/useFaqCapabilities'
import QuickAnswerEditor from '../components/quickAnswer/QuickAnswerEditor'
import QuickAnswerList from '../components/quickAnswer/QuickAnswerList'
import {
  DEFAULT_LIMITS, draftFromEntry, draftProblems, emptyDraft, enableBlocker, entryIdFromLocation, isDirty,
} from '../components/quickAnswer/quickAnswerRules'
import {
  RETRYABLE_QUICK_ANSWER_ERRORS, createQuickAnswer, deleteQuickAnswer, getQuickAnswers, quickAnswerErrorCode,
  quickAnswerErrorField, quickAnswerErrorMessage, updateQuickAnswer,
} from '../api/quickAnswers'
import useManagerStore from '../store/managerStore'
import { toast } from 'react-toastify'

function Notice({ tone = 'slate', children }) {
  const tones = {
    slate: 'border-slate-200 bg-white text-slate-500',
    blue: 'border-blue-200 bg-blue-50 text-blue-800',
    red: 'border-red-200 bg-red-50 text-red-700',
  }
  return <div className={`rounded-lg border p-4 text-sm ${tones[tone]}`}>{children}</div>
}

const FIELDS = ['title', 'description', 'text', 'attachments']

export default function QuickAnswerPage() {
  const { t } = useTranslation()
  const tenantId = useManagerStore((s) => s.tenantId)
  const { capabilities, loading: capsLoading, error: capsError, refetch: refetchCaps } = useFaqCapabilities(tenantId)
  const [attempt, setAttempt] = useState(0)
  const [state, setState] = useState({ key: null, data: null, error: null })
  const key = tenantId ? `${tenantId}:${attempt}` : null

  // Selection: 'new' (a blank draft), an entry id, or null. `saved` is what the draft is compared with.
  const [selectedId, setSelectedId] = useState(null)
  const [draft, setDraft] = useState(emptyDraft)
  const [saved, setSaved] = useState(emptyDraft)
  const [busy, setBusy] = useState(false)
  const [fieldErrors, setFieldErrors] = useState({})
  const [notFound, setNotFound] = useState(false)
  const dirty = isDirty(draft, saved)
  const dirtyRef = useRef(false)
  dirtyRef.current = dirty

  const open = useCallback((id, items) => {
    const entry = id && id !== 'new' ? items.find((item) => item.id === id) : null
    const next = entry ? draftFromEntry(entry) : emptyDraft()
    setSelectedId(entry ? entry.id : id === 'new' ? 'new' : null)
    setDraft(next)
    setSaved(next)
    setFieldErrors({})
  }, [])

  useEffect(() => {
    if (!key) return undefined
    let alive = true
    getQuickAnswers(tenantId)
      .then((data) => {
        if (!alive) return
        const items = data.items || []
        const target = entryIdFromLocation(window.location)
        const found = items.find((item) => item.id === target)
        setNotFound(Boolean(target) && !found)
        open(found ? found.id : items[0]?.id ?? null, items)
        setState({ key, data, error: null })
      })
      .catch((error) => alive && setState({ key, data: null, error }))
    return () => { alive = false }
  }, [tenantId, key, open])

  const retry = useCallback(() => { setAttempt((n) => n + 1); refetchCaps() }, [refetchCaps])
  const settled = state.key === key
  const data = settled ? state.data : null
  const loadError = settled ? state.error : null
  const items = data?.items || []
  const setItems = (update) => setState((previous) => ({
    ...previous, data: { ...previous.data, items: update(previous.data.items || []) },
  }))

  const confirmLeave = () => !dirtyRef.current || window.confirm(t('quickAnswer.editor.unsaved'))
  const select = (id) => {
    if (id === selectedId || (id !== 'new' && !items.some((item) => item.id === id))) return
    if (!confirmLeave()) return false
    setNotFound(false)
    open(id, items)
    return true
  }
  const selectRef = useRef(select)
  selectRef.current = select
  const selectedIdRef = useRef(null)
  selectedIdRef.current = selectedId

  useEffect(() => {
    const onHash = () => {
      const id = entryIdFromLocation(window.location)
      if (!id) return
      // Declined the unsaved-changes confirm: put the hash back on the entry the editor still shows.
      if (selectRef.current(id) === false) {
        const shown = selectedIdRef.current
        const hash = shown && shown !== 'new' ? `#${shown}` : ''
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${hash}`)
      }
    }
    const onBeforeUnload = (event) => { if (dirtyRef.current) event.preventDefault() }
    window.addEventListener('hashchange', onHash)
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => {
      window.removeEventListener('hashchange', onHash)
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, [])

  const limits = { ...DEFAULT_LIMITS, ...(data?.limits || {}) }
  const canWrite = Boolean(data?.can_write)
  const problems = draftProblems(draft, limits)

  const fail = (error) => {
    toast.error(quickAnswerErrorMessage(t, error))
    const code = quickAnswerErrorCode(error)
    const field = quickAnswerErrorField(error)
    setFieldErrors(FIELDS.includes(field) ? { [field]: code } : {})
    if (['entry_not_found', 'too_many_entries', 'permission_denied'].includes(code)) {
      setDraft(saved) // the page reloads: nothing to keep
      retry()
    }
  }

  const save = async () => {
    setBusy(true)
    setFieldErrors({})
    const body = {
      title: draft.title.trim(), description: draft.description.trim(), text: draft.text, attachments: draft.attachments,
    }
    try {
      if (selectedId === 'new') {
        const entry = await createQuickAnswer(tenantId, body)
        setItems((list) => [...list, entry])
        const next = draftFromEntry(entry)
        setSelectedId(entry.id)
        setDraft(next)
        setSaved(next)
        toast.success(t('quickAnswer.editor.created'))
      } else {
        const entry = await updateQuickAnswer(tenantId, selectedId, { ...body, enabled: draft.enabled })
        setItems((list) => list.map((item) => (item.id === entry.id ? entry : item)))
        const next = draftFromEntry(entry)
        setDraft(next)
        setSaved(next)
        toast.success(t('quickAnswer.editor.saved'))
      }
    } catch (error) {
      fail(error)
    } finally {
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!window.confirm(t('quickAnswer.editor.confirmDelete', { title: draft.title }))) return
    setBusy(true)
    try {
      await deleteQuickAnswer(tenantId, selectedId)
      const rest = items.filter((item) => item.id !== selectedId)
      setItems(() => rest)
      open(rest[0]?.id ?? null, rest)
      toast.success(t('quickAnswer.editor.deleted'))
    } catch (error) {
      fail(error)
    } finally {
      setBusy(false)
    }
  }

  const applyStarter = (starter) => {
    const typed = draft.title || draft.description || draft.text
    if (typed && !window.confirm(t('quickAnswer.editor.confirmStarter'))) return
    setDraft((previous) => ({ ...previous, title: starter.title, description: starter.description, text: starter.text }))
  }

  let body
  if (!tenantId) {
    body = <Notice>{t('quickAnswer.needTenant')}</Notice>
  } else if ((!settled || capsLoading) && !data) {
    body = <Notice>{t('common.loading')}</Notice>
  } else if (loadError || capsError) {
    const error = loadError || capsError
    body = (
      <Notice tone="red">
        <span>{quickAnswerErrorMessage(t, error)}</span>
        {RETRYABLE_QUICK_ANSWER_ERRORS.has(quickAnswerErrorCode(error)) && (
          <button type="button" onClick={retry} className="ml-3 font-semibold underline underline-offset-2">
            {t('common.retry')}
          </button>
        )}
      </Notice>
    )
  } else if (capabilities && capabilities.quick_answers === false) {
    body = <Notice>{t('quickAnswer.notEnabled')}</Notice>
  } else if (data) {
    const entry = items.find((item) => item.id === selectedId)
    const isNew = selectedId === 'new'
    body = (
      <>
        {data.mode === 'shadow' && <Notice tone="blue">{t('quickAnswer.shadowNotice')}</Notice>}
        {!canWrite && <Notice>{t('quickAnswer.viewOnly')}</Notice>}
        {notFound && <Notice>{t('quickAnswer.editor.notFound')}</Notice>}
        <div className="grid gap-4 lg:grid-cols-[260px_minmax(0,1fr)]">
          <QuickAnswerList
            items={items}
            selectedId={selectedId}
            newDraftOpen={isNew}
            newDraftTitle={isNew ? draft.title : ''}
            maxEntries={limits.max_entries}
            canWrite={canWrite}
            onSelect={select}
            onAdd={() => select('new')}
          />
          {selectedId ? (
            <QuickAnswerEditor
              key={selectedId}
              tenantId={tenantId}
              draft={draft}
              isNew={isNew}
              dirty={dirty}
              canWrite={canWrite}
              busy={busy}
              limits={limits}
              starters={data.starters || []}
              warnings={entry?.warnings || []}
              fieldErrors={fieldErrors}
              canSave={Object.keys(problems).length === 0 && !(draft.enabled && enableBlocker(draft.text))}
              onChange={(changes) => { setDraft((previous) => ({ ...previous, ...changes })); setFieldErrors({}) }}
              onStarter={applyStarter}
              onSave={save}
              onDelete={remove}
              onBusy={setBusy}
            />
          ) : (
            <Notice>{t('quickAnswer.editor.emptyPick')}</Notice>
          )}
        </div>
      </>
    )
  }

  return (
    <ResponsiveShell>
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 p-4">
        <div>
          <p className="text-xs font-semibold uppercase text-slate-400">{t('quickAnswer.breadcrumb')}</p>
          <h1 className="text-xl font-semibold text-slate-900">{t('quickAnswer.title')}</h1>
          <p className="text-sm text-slate-500">{t('quickAnswer.helper')}</p>
        </div>
        {body}
      </div>
    </ResponsiveShell>
  )
}
